import * as React from "react";
import {Box, Tooltip} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import {forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from "react";
import {ARContext} from "./AutoRefresh";
import {DGContext} from "./raal_components/grid/DataGrid";
import useSound from 'use-sound';
import {makeStyles} from "@material-ui/core/styles";
import {createStyles} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import DataStorage from "../common/DataStorage";
import {isMobile} from "react-device-detect";
import {CrudUpdateEvent} from "./model/CrudUpdate";

const useStyles = makeStyles((theme) =>
    createStyles({
        alert: {
            transition: "0.3s all ease",
            display: "flex",
            borderRadius: "50%",
            padding: "4px",
            marginRight: "10px",
        },
        alertDisabled: {
            backgroundColor: theme.palette.type === 'dark' ? "#333333" : "#e6e6e6",
        },
        alertEnabled: {
            backgroundColor: theme.palette.type === 'dark' ? "#b15800" : "#ffbf77",
        },
        alertLocked: {
            backgroundColor: theme.palette.type === 'dark' ? "#bb0000" : "#ff4d4d",
        },
    }),
);

const isAudioLocked = () : Promise<boolean> => {
    return new Promise(resolve => {
        const checkHTML5Audio = async () => {
            const audio = new Audio();
            try {
                await audio.play();
                resolve(false);
            } catch (err) {
                resolve(true);
            }
        };
        try {
            const context = new (window.AudioContext)();
            setTimeout(() => {
                if (context.state === "suspended") {
                    // Musime cakat na dostupny kontext nakolko niekedy na mobilnych zariadeniach
                    // zrejme kvoli blokovanemu hlavnemu vlaknu alebo mobilom je nedostupne
                    context.resume().then(() => resolve(context.state === 'suspended'))
                }
            }, 100);
        } catch (e) {
            checkHTML5Audio().then();
        }
    });
};

const key = '_alertEnabled';

export const RefreshAlert = forwardRef((_props, ref) => {
    const {table, setState, name} = useContext(DGContext);
    const [audioLocked, setAudioLocked] = useState(false);
    // Kvoli autoplay politike na Androide a iOS musime donutit uzivatela zapinat notifikacie vzdy po loade view
    const [firstMobileRender, setFirstMobileRender] = useState(isMobile);
    const [alertEnabled, setAlertEnabled] = useState(
        firstMobileRender ? false : (DataStorage.get(`${name}${key}`, false, "session") ?? `false`) === 'true'
    );
    const [arState] = useContext(ARContext);
    const {enabled, count, pushNotification} = arState;
    const lastCount = useRef(0);
    const canPlayNotification = useRef(true);
    const [playNotification] = useSound("/sounds/notification.mp3");
    const classes = useStyles();
    const {t} = useTranslation();

    // Sluzi na synchronizaciu countera u tlacitka NOVA DATA
    useImperativeHandle(ref, () => ({
        setValue: (newValue: number) => { lastCount.current = newValue; },
    }));

    useEffect(()=> {
        (async () => {
            setAudioLocked(await isAudioLocked());
        })();
    });

    useEffect(()=>{
        if(!audioLocked && alertEnabled && pushNotification && canPlayNotification.current && count > lastCount.current){
            playNotification();
            canPlayNotification.current = false;
            setTimeout(() => {
                canPlayNotification.current = true;
            }, 5000);
        }
    }, [enabled, count, setState, table, audioLocked, alertEnabled, playNotification]);

    useEffect(()=>{
        DataStorage.set(`${name}${key}`, alertEnabled, false, "session");
    }, [alertEnabled, name]);

    return <Box className={`${classes.alert} ${alertEnabled ? (firstMobileRender ? classes.alertDisabled : (audioLocked ? classes.alertLocked : classes.alertEnabled)) : classes.alertDisabled}`}>
        <Tooltip
            title={t(alertEnabled ? (audioLocked ? "RefreshAlert.TooltipInactive" : "RefreshAlert.TooltipOn")  : "RefreshAlert.TooltipOff")}
            key={alertEnabled ? "notificationOn" : "notificationOff"}
            onClick={() => {
                if(audioLocked) setAudioLocked(false); else setAlertEnabled(!alertEnabled);
                setFirstMobileRender(false);
            }}
            placement={"bottom-start"}
        >
            {firstMobileRender ? <NotificationsOffIcon fontSize={"small"}/> : (alertEnabled && !audioLocked ? <NotificationsActiveIcon fontSize={"small"}/> : <NotificationsOffIcon fontSize={"small"}/>)}
        </Tooltip>

    </Box>
})